import React from 'react';
import styled from 'styled-components';
import Hero from '../components/Hero';
import ServiceDetail from '../components/ServiceDetail';
import ContactForm from '../components/ContactForm';
import SectionTitle from '../components/SectionTitle';
import SEO from '../components/SEO';
import { djServicesImage, djServicesDetailImage } from '../assets/images';
import { FaMusic, FaGlassCheers, FaBriefcase, FaRing } from 'react-icons/fa';

const DJServices = () => {
  const features = [
    'Professional DJ services for weddings, corporate events, nightclubs, and private parties',
    'Extensive music library spanning all genres and decades',
    'State-of-the-art sound and lighting equipment',
    'Personalized playlists tailored to your event',
    'Seamless mixing and beat matching',
    'Professional MC services available',
    'Backup equipment for peace of mind',
    'Early setup and sound check'
  ];

  return (
    <main>
      <SEO 
        title="DJ Services | TK DJ Services"
        description="Professional DJ services for weddings, corporate events, nightclubs, and private parties in the DMV area. Experienced DJ with versatile music selection."
        url="/dj-services"
        canonical="/dj-services"
        image="/images/AdobeStock_443459843.jpeg"
      />
      <Hero 
        backgroundImage={djServicesImage}
        title="DJ Services"
        subtitle="Elevate your event with our professional DJ services, tailored to create the perfect atmosphere for any occasion."
        buttonText="Book Now"
        buttonLink="#contact"
      />
      
      <ServiceDetailSection>
        <div className="container">
          <ServiceDetail 
            title="Exceptional Music Experience for Every Event"
            description="With over 20 years of experience, TK DJ Services provides top-tier DJ entertainment for a wide range of events. From weddings and corporate functions to nightclubs and private parties, we bring the perfect musical atmosphere to every occasion. Our extensive music library, professional-grade equipment, and expert mixing skills ensure your event has the perfect soundtrack."
            features={features}
            image={djServicesDetailImage}
            ctaText="Book Your Event"
            ctaLink="#contact"
          />
        </div>
      </ServiceDetailSection>
      
      <SpecialtySection>
        <div className="container">
          <SectionTitle 
            title="Our DJ Specialties" 
            subtitle="We offer specialized DJ services for various types of events, each with a unique approach to create the perfect atmosphere."
            center
          />
          
          <SpecialtyGrid>
            <SpecialtyCard>
              <SpecialtyIcon>
                <FaRing />
              </SpecialtyIcon>
              <SpecialtyTitle>Weddings</SpecialtyTitle>
              <SpecialtyDescription>
                From the ceremony to the last dance, we create a personalized musical journey for your special day. Our wedding DJ services include detailed planning, elegant MC services, and a perfect blend of music to keep all generations on the dance floor.
              </SpecialtyDescription>
            </SpecialtyCard>
            
            <SpecialtyCard>
              <SpecialtyIcon>
                <FaBriefcase />
              </SpecialtyIcon>
              <SpecialtyTitle>Corporate Events</SpecialtyTitle>
              <SpecialtyDescription>
                We understand the unique requirements of corporate functions. Our professional approach ensures appropriate music selection, clear announcements, and a sophisticated atmosphere that aligns with your company's image and event objectives.
              </SpecialtyDescription>
            </SpecialtyCard>
            
            <SpecialtyCard>
              <SpecialtyIcon>
                <FaGlassCheers />
              </SpecialtyIcon>
              <SpecialtyTitle>Private Parties</SpecialtyTitle>
              <SpecialtyDescription>
                Birthday celebrations, anniversaries, or holiday parties—we bring the energy and musical expertise to make your private event memorable. We work closely with you to create the perfect playlist and atmosphere for your special occasion.
              </SpecialtyDescription>
            </SpecialtyCard>
            
            <SpecialtyCard>
              <SpecialtyIcon>
                <FaMusic />
              </SpecialtyIcon>
              <SpecialtyTitle>Nightclubs & Lounges</SpecialtyTitle>
              <SpecialtyDescription>
                With extensive experience in the DMV's nightlife scene, we deliver cutting-edge sets that keep the dance floor packed. Our expertise in reading the crowd and seamless mixing creates an unforgettable nightclub experience.
              </SpecialtyDescription>
            </SpecialtyCard>
          </SpecialtyGrid>
        </div>
      </SpecialtySection>
      
      <ContactSection id="contact">
        <div className="container">
          <SectionTitle 
            title="Book Your Event" 
            subtitle="Ready to elevate your event with professional DJ services? Contact us today to check availability and discuss your specific needs."
            center
          />
          <ContactForm />
        </div>
      </ContactSection>
    </main>
  );
};

const ServiceDetailSection = styled.section`
  padding: 8rem 0 5rem;
`;

const SpecialtySection = styled.section`
  padding: 5rem 0;
  background-color: rgba(0, 0, 0, 0.3);
`;

const SpecialtyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SpecialtyCard = styled.div`
  background-color: rgba(19, 19, 19, 0.5);
  border-radius: 8px;
  padding: 2rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const SpecialtyIcon = styled.div`
  font-size: 2.5rem;
  color: var(--color-secondary);
  margin-bottom: 1.5rem;
`;

const SpecialtyTitle = styled.h3`
  color: var(--color-tertiary);
  margin-bottom: 1rem;
`;

const SpecialtyDescription = styled.p`
  color: var(--color-tertiary);
  opacity: 0.8;
  line-height: 1.6;
`;

const ContactSection = styled.section`
  padding: 5rem 0;
  
  .container {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

export default DJServices; 